// MITRE ATT&CK Enterprise Matrix Data
// Source: https://attack.mitre.org/matrices/enterprise/

export const TACTICS = [
  {
    id: 'TA0043',
    name: 'Reconnaissance',
    shortName: 'Recon',
    description: 'The adversary is trying to gather information they can use to plan future operations.',
    color: '#6366f1',
    order: 1
  },
  {
    id: 'TA0042',
    name: 'Resource Development',
    shortName: 'Resource Dev',
    description: 'The adversary is trying to establish resources they can use to support operations.',
    color: '#4f46e5',
    order: 2
  },
  {
    id: 'TA0001',
    name: 'Initial Access',
    shortName: 'Initial Access',
    description: 'The adversary is trying to get into your network.',
    color: '#4338ca',
    order: 3
  },
  {
    id: 'TA0002',
    name: 'Execution',
    shortName: 'Execution',
    description: 'The adversary is trying to run malicious code.',
    color: '#3b82f6',
    order: 4
  },
  {
    id: 'TA0003',
    name: 'Persistence',
    shortName: 'Persistence',
    description: 'The adversary is trying to maintain their foothold.',
    color: '#2563eb',
    order: 5
  },
  {
    id: 'TA0004',
    name: 'Privilege Escalation',
    shortName: 'Priv Esc',
    description: 'The adversary is trying to gain higher-level permissions.',
    color: '#1d4ed8',
    order: 6
  },
  {
    id: 'TA0005',
    name: 'Defense Evasion',
    shortName: 'Defense Evasion',
    description: 'The adversary is trying to avoid being detected.',
    color: '#0ea5e9',
    order: 7
  },
  {
    id: 'TA0006',
    name: 'Credential Access',
    shortName: 'Cred Access',
    description: 'The adversary is trying to steal account names and passwords.',
    color: '#0284c7',
    order: 8
  },
  {
    id: 'TA0007',
    name: 'Discovery',
    shortName: 'Discovery',
    description: 'The adversary is trying to figure out your environment.',
    color: '#818cf8',
    order: 9
  },
  {
    id: 'TA0008',
    name: 'Lateral Movement',
    shortName: 'Lateral Move',
    description: 'The adversary is trying to move through your environment.',
    color: '#6366f1',
    order: 10
  },
  {
    id: 'TA0009',
    name: 'Collection',
    shortName: 'Collection',
    description: 'The adversary is trying to gather data of interest.',
    color: '#4f46e5',
    order: 11
  },
  {
    id: 'TA0011',
    name: 'Command and Control',
    shortName: 'C2',
    description: 'The adversary is trying to communicate with compromised systems.',
    color: '#3730a3',
    order: 12
  },
  {
    id: 'TA0010',
    name: 'Exfiltration',
    shortName: 'Exfiltration',
    description: 'The adversary is trying to steal data.',
    color: '#312e81',
    order: 13
  },
  {
    id: 'TA0040',
    name: 'Impact',
    shortName: 'Impact',
    description: 'The adversary is trying to manipulate, interrupt, or destroy your systems and data.',
    color: '#1e1b4b',
    order: 14
  }
];

// Common techniques mapped to tools/commands
export const TECHNIQUES = {
  // Reconnaissance
  'T1595.001': {
    id: 'T1595.001',
    name: 'Active Scanning: Scanning IP Blocks',
    tacticId: 'TA0043',
    tools: ['nmap', 'masscan', 'zmap'],
    description: 'Adversaries may scan victim IP blocks to gather information.'
  },
  'T1595.002': {
    id: 'T1595.002',
    name: 'Active Scanning: Vulnerability Scanning',
    tacticId: 'TA0043',
    tools: ['nessus', 'openvas', 'nuclei'],
    description: 'Adversaries may scan victims for vulnerabilities.'
  },
  'T1590.005': {
    id: 'T1590.005',
    name: 'Gather Victim Network Information: IP Addresses',
    tacticId: 'TA0043',
    tools: ['whois', 'dig', 'host'],
    description: 'Adversaries may gather the victim\'s IP addresses.'
  },
  'T1592.002': {
    id: 'T1592.002',
    name: 'Gather Victim Host Information: Software',
    tacticId: 'TA0043',
    tools: ['nmap', 'whatweb', 'wappalyzer'],
    description: 'Adversaries may gather information about the victim\'s host software.'
  },
  'T1589.001': {
    id: 'T1589.001',
    name: 'Gather Victim Identity Information: Credentials',
    tacticId: 'TA0043',
    tools: ['theHarvester', 'sherlock', 'haveibeenpwned'],
    description: 'Adversaries may gather credentials that can be used during targeting.'
  },

  // Initial Access
  'T1190': {
    id: 'T1190',
    name: 'Exploit Public-Facing Application',
    tacticId: 'TA0001',
    tools: ['metasploit', 'sqlmap', 'burpsuite'],
    description: 'Adversaries may attempt to exploit a weakness in an Internet-facing computer or program.'
  },
  'T1133': {
    id: 'T1133',
    name: 'External Remote Services',
    tacticId: 'TA0001',
    tools: ['hydra', 'medusa', 'patator'],
    description: 'Adversaries may leverage external-facing remote services to initially access and/or persist.'
  },
  'T1566.001': {
    id: 'T1566.001',
    name: 'Phishing: Spearphishing Attachment',
    tacticId: 'TA0001',
    tools: ['gophish', 'setoolkit'],
    description: 'Adversaries may send spearphishing emails with a malicious attachment.'
  },

  // Execution
  'T1059.001': {
    id: 'T1059.001',
    name: 'Command and Scripting Interpreter: PowerShell',
    tacticId: 'TA0002',
    tools: ['powershell', 'empire', 'cobalt-strike'],
    description: 'Adversaries may abuse PowerShell commands and scripts for execution.'
  },
  'T1059.004': {
    id: 'T1059.004',
    name: 'Command and Scripting Interpreter: Unix Shell',
    tacticId: 'TA0002',
    tools: ['bash', 'sh', 'nc'],
    description: 'Adversaries may abuse Unix shell commands and scripts for execution.'
  },

  // Persistence
  'T1053.003': {
    id: 'T1053.003',
    name: 'Scheduled Task/Job: Cron',
    tacticId: 'TA0003',
    tools: ['crontab'],
    description: 'Adversaries may abuse the cron utility to perform task scheduling for initial or recurring execution.'
  },

  // Privilege Escalation
  'T1068': {
    id: 'T1068',
    name: 'Exploitation for Privilege Escalation',
    tacticId: 'TA0004',
    tools: ['linux-exploit-suggester', 'linpeas'],
    description: 'Adversaries may exploit software vulnerabilities to elevate privileges.'
  },

  // Defense Evasion
  'T1070.004': {
    id: 'T1070.004',
    name: 'Indicator Removal: File Deletion',
    tacticId: 'TA0005',
    tools: ['rm', 'shred'],
    description: 'Adversaries may delete files to hide their presence.'
  },

  // Credential Access
  'T1110.001': {
    id: 'T1110.001',
    name: 'Brute Force: Password Guessing',
    tacticId: 'TA0006',
    tools: ['hydra', 'medusa', 'crackmapexec'],
    description: 'Adversaries may use brute force techniques to gain access.'
  },
  'T1555.003': {
    id: 'T1555.003',
    name: 'Credentials from Password Stores: Credentials from Web Browsers',
    tacticId: 'TA0006',
    tools: ['lazagne', 'mimikatz'],
    description: 'Adversaries may acquire credentials from web browsers.'
  },

  // Discovery
  'T1018': {
    id: 'T1018',
    name: 'Remote System Discovery',
    tacticId: 'TA0007',
    tools: ['nmap', 'arp-scan', 'netdiscover'],
    description: 'Adversaries may attempt to get a listing of other systems.'
  },
  'T1082': {
    id: 'T1082',
    name: 'System Information Discovery',
    tacticId: 'TA0007',
    tools: ['uname', 'systeminfo'],
    description: 'Adversaries may attempt to get detailed information about the operating system.'
  },

  // Lateral Movement
  'T1021.001': {
    id: 'T1021.001',
    name: 'Remote Services: Remote Desktop Protocol',
    tacticId: 'TA0008',
    tools: ['rdesktop', 'xfreerdp'],
    description: 'Adversaries may use RDP to move laterally.'
  },
  'T1021.004': {
    id: 'T1021.004',
    name: 'Remote Services: SSH',
    tacticId: 'TA0008',
    tools: ['ssh', 'sshpass'],
    description: 'Adversaries may use SSH to move laterally.'
  },

  // Collection
  'T1560.001': {
    id: 'T1560.001',
    name: 'Archive Collected Data: Archive via Utility',
    tacticId: 'TA0009',
    tools: ['tar', 'zip', '7z'],
    description: 'Adversaries may use utilities to compress or encrypt data.'
  },

  // Command and Control
  'T1071.001': {
    id: 'T1071.001',
    name: 'Application Layer Protocol: Web Protocols',
    tacticId: 'TA0011',
    tools: ['nc', 'socat', 'metasploit'],
    description: 'Adversaries may communicate using application layer protocols.'
  },

  // Exfiltration
  'T1041': {
    id: 'T1041',
    name: 'Exfiltration Over C2 Channel',
    tacticId: 'TA0010',
    tools: ['nc', 'curl', 'wget'],
    description: 'Adversaries may steal data by exfiltrating it over an existing C2 channel.'
  },

  // Impact
  'T1486': {
    id: 'T1486',
    name: 'Data Encrypted for Impact',
    tacticId: 'TA0040',
    tools: ['openssl', 'gpg'],
    description: 'Adversaries may encrypt data on target systems to interrupt availability.'
  },
  'T1485': {
    id: 'T1485',
    name: 'Data Destruction',
    tacticId: 'TA0040',
    tools: ['dd', 'shred'],
    description: 'Adversaries may destroy data and files to disrupt operations.'
  }
};

// Helper functions
export const getTacticById = (tacticId) => {
  return TACTICS.find(t => t.id === tacticId);
};

export const getTechniqueById = (techniqueId) => {
  return TECHNIQUES[techniqueId];
};

export const getTechniquesByTactic = (tacticId) => {
  return Object.values(TECHNIQUES).filter(t => t.tacticId === tacticId);
};

export const getTacticColor = (tacticId) => {
  const tactic = getTacticById(tacticId);
  return tactic ? tactic.color : '#667eea';
};

export const getTacticProgress = (techniques) => {
  if (!techniques || techniques.length === 0) return 0;
  const completed = techniques.filter(t => t.status === 'completed').length;
  return (completed / techniques.length) * 100;
};

